<template>
  <div>
    <v-btn small color="primary" @click="open" v-if="showTrigger">Manage Documents</v-btn>

    <v-dialog v-model="visible" max-width="1100px" persistent>
      <v-card>
        <v-toolbar flat color="white">
          <v-toolbar-title>Repair Documents</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text>
          <!-- Upload area -->
          <div
            class="upload-dropzone mb-4"
            :class="{ 'upload-dropzone--dragover': isDragOver }"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
          >
            <div class="d-flex flex-column align-center justify-center py-8">
              <v-icon large color="primary">mdi-cloud-upload</v-icon>
              <div class="subtitle-2 mt-2">Drag and drop files here, or</div>
              <v-file-input
                class="mt-2"
                v-model="fileInputBuffer"
                multiple
                show-size
                truncate-length="45"
                accept="image/*,application/pdf"
                label="Browse files"
                prepend-icon="mdi-folder-open"
                @change="onFilesSelected"
              />
              <div class="subtitle-2 mt-2">or</div>
              <!-- Take photo button -->
              <v-btn class="mt-2" color="primary" @click="openCamera">
                <v-icon left>mdi-camera</v-icon>
                Take photo
              </v-btn>

              <!-- Hidden native input that requests the camera on mobile -->
              <input
                ref="cameraInput"
                type="file"
                accept="image/*"
                capture="environment"
                style="display: none"
                @change="onCameraCaptured"
              />
              <div class="caption grey--text mt-1">Supported: images and PDF</div>
            </div>
          </div>

          <!-- Pending files chips -->
          <div v-if="pendingFiles.length" class="mb-4">
            <div class="subtitle-2 mb-2">Pending files</div>
            <v-chip-group multiple column>
              <v-chip
                v-for="pf in pendingFiles"
                :key="pf._id"
                class="mr-2 mb-2"
                label
                outlined
              >
                <v-avatar left>
                  <v-icon small v-if="pf.type === 'pdf'">mdi-file-pdf-box</v-icon>
                  <v-icon small v-else-if="pf.type === 'image'">mdi-image</v-icon>
                  <v-icon small v-else>mdi-file</v-icon>
                </v-avatar>
                <span class="text-truncate" style="max-width: 220px">{{ pf.file.name }}</span>
                <v-spacer></v-spacer>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop="openEditPending(pf)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit metadata</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop="removePending(pf._id)">
                      <v-icon small color="red">mdi-trash-can</v-icon>
                    </v-btn>
                  </template>
                  <span>Remove</span>
                </v-tooltip>
              </v-chip>
            </v-chip-group>

            <div class="text-right mt-2">
              <v-btn :loading="uploading" color="success" @click="uploadPending" :disabled="!pendingFiles.length">
                <v-icon left>mdi-cloud-upload</v-icon>
                Upload {{ pendingFiles.length }} file<span v-if="pendingFiles.length > 1">s</span>
              </v-btn>
            </div>
          </div>

          <!-- Grid of existing items -->
          <div class="mb-2 d-flex align-center justify-space-between">
            <div class="subtitle-2">Existing documents</div>
            <div class="d-flex align-center">
              <v-text-field
                v-model="filters.q"
                label="Search filename or metadata"
                append-icon="mdi-magnify"
                @input="applyFilters"
                dense
                clearable
                hide-details
                class="mr-4"
                style="max-width: 320px"
              />
              <v-select
                v-model="filters.type"
                :items="fileTypeOptions"
                label="File type"
                dense
                clearable
                hide-details
                @change="applyFilters"
                style="max-width: 160px"
              />
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn text v-bind="attrs" v-on="on" small class="ml-2">
                    Sort: {{ sort.label }}
                    <v-icon right>mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item v-for="option in sortOptions" :key="option.value" @click="setSort(option)">
                    <v-list-item-title>{{ option.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>

          <v-row>
            <v-col cols="12">
              <v-row dense>
                <v-col v-for="item in pagedItems" :key="item._localId" cols="12" sm="6" md="4" lg="3">
                  <v-card elevation="1">
                    <!-- Thumbnail without tooltip -->
                    <v-sheet height="160" class="d-flex align-center justify-center" tile>
                      <template v-if="isImage(item)">
                        <v-img
                          lazy-src="../assets/logo_fm_app_l.png"
                          :src="item.thumbnail || item.downloadUrl"
                          :alt="item.filename"
                          contain
                          max-height="90%"
                        >
                          <template v-slot:placeholder>
                            <v-row class="fill-height ma-0" align="center" justify="center">
                              <v-progress-circular indeterminate color="grey lighten-5" />
                            </v-row>
                          </template>
                        </v-img>
                      </template>
                    
                      <template v-else-if="isPdf(item)">
                        <v-img
                          lazy-src="../assets/logo_fm_app_l.png"
                          :src="item.thumbnail || item.downloadUrl"
                          :alt="item.filename"
                          contain
                          max-height="90%"
                        >
                          <template v-slot:placeholder>
                            <v-row class="fill-height ma-0" align="center" justify="center">
                              <v-progress-circular indeterminate color="grey lighten-5" />
                            </v-row>
                          </template>
                        </v-img>
                      </template>
                    
                      <template v-else>
                        <v-icon x-large>mdi-file-document-outline</v-icon>
                      </template>
                    </v-sheet>
                    
                   <!-- Title with tooltip for notes -->
                   <v-card-title class="subtitle-2">
                     <template v-if="item.notes">
                       <v-tooltip top>
                         <template v-slot:activator="{ on, attrs }">
                           <div
                             class="text-truncate"
                             style="max-width:220px"
                             v-bind="attrs"
                             v-on="on"
                           >
                             {{ item.filename }}
                           </div>
                         </template>
                         <span style="white-space: pre-wrap;">{{ item.notes }}</span>
                       </v-tooltip>
                     </template>
                     <template v-else>
                       <div class="text-truncate" style="max-width:220px">
                         {{ item.filename }}
                       </div>
                     </template>
                   </v-card-title>
                    <!-- Subtitle + Tag chip -->
                    <v-card-subtitle>
                      <div class="grey--text text--darken-1 caption">
                        {{ item.fileType || 'file' }} • {{ formatDate(item.fetched_at) }}
                      </div>
                      <div v-if="typeof item.tag === 'string' && item.tag" class="mt-1">
                        <v-chip small label>{{ item.tag }}</v-chip>
                      </div>
                    </v-card-subtitle>

                    <!-- Actions remain unchanged -->
                    <v-card-actions>
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn small icon v-bind="attrs" v-on="on" @click="previewItem(item)" :title="'Preview ' + item.filename">
                            <v-icon>mdi-eye</v-icon>
                          </v-btn>
                        </template>
                        <span>Preview</span>
                      </v-tooltip>

                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn small icon v-bind="attrs" v-on="on" @click="downloadItem(item)" :title="'Download ' + item.filename">
                            <v-icon>mdi-download</v-icon>
                          </v-btn>
                        </template>
                        <span>Download</span>
                      </v-tooltip>

                     <v-btn
                        small
                        icon
                        v-bind="attrs"
                        v-on="on"
                        @click="openEditMetadataDialog(item)"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>

                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            small
                            icon
                            v-bind="attrs"
                            v-on="on"
                            @click="deleteItem(item)"
                            :title="'Delete ' + item.filename"
                          >
                            <v-icon color="red">mdi-trash-can</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete</span>
                      </v-tooltip>

                      <v-spacer></v-spacer>

                      <v-menu bottom right>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
                        </template>
                        <v-list>
                          <v-list-item @click="copyUrl(item)"><v-list-item-title>Copy URL</v-list-item-title></v-list-item>
                        </v-list>
                      </v-menu>
                    </v-card-actions> 
                  </v-card>
                </v-col>
              </v-row>

              <v-row justify="center" class="mt-4">
                <v-pagination v-model="page" :length="pageCount" circle></v-pagination>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>


        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="close">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  <!-- Pending file metadata editor -->
  <v-dialog v-model="editPendingDialog.visible" max-width="560px">
    <v-card>
      <v-card-title class="headline">Edit Metadata</v-card-title>
      <v-card-text>
        <!-- Image preview (only for image files) -->
          <div v-if="isImageFile && previewUrl" class="mb-4 text-center">
            <v-img
              :src="previewUrl"
              max-height="180"
              contain
              class="grey lighten-3"
            />
          </div>


        <!-- Filename -->
        <div class="subtitle-2 grey--text text--darken-1 mb-1">Filename</div>
        <div class="mb-4">
          <v-text-field
            v-model="editPendingDialog.meta.filename"
            label="Filename"
            dense
            clearable
          />
        </div>

        <!-- Tag -->
        <div class="subtitle-2 grey--text text--darken-1 mb-1">Tag</div>
        <div class="mb-4">
        <v-chip-group
            v-model="tagIndex"
            active-class="primary--text"
            column
          >
            <v-chip
              v-for="(tag, index) in defaultTags"
              :key="tag"
              :value="index"
              outlined
              clickable
              class="mr-2 mb-2"
            >
              {{ tag }}
            </v-chip>

            <v-menu offset-y v-model="tagAddMenu" :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  class="mr-2 mb-2"
                  clickable
                >
                  <v-icon small>mdi-plus</v-icon>
                </v-chip>
              </template>

              <v-card>
                <v-card-text>
                  <v-text-field
                    dense
                    label="New tag"
                    v-model="newTag"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="tagAddMenu = false">Cancel</v-btn>
                  <v-btn text @click="addNewTag">Add</v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </v-chip-group>
        </div>

        <!-- Notes -->
        <v-textarea
          v-model="editPendingDialog.meta.notes"
          label="Notes"
          auto-grow
          rows="3"
          dense
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="editPendingDialog.visible = false">Cancel</v-btn>
        <v-btn color="primary" @click="savePendingMeta">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog> 

  <v-dialog v-model="editMetadataDialog" max-width="600px">
    <v-card>
      <v-card-title>
        Edit Document Metadata
      </v-card-title>

      <v-card-text>
        <v-form ref="editMetadataFormRef">
          <v-text-field
            v-model="editMetadataForm.filename"
            label="Filename"
            dense
            outlined
          />
          <v-text-field
            v-model="editMetadataForm.tag"
            label="Tag"
            dense
            outlined
          />
          <v-textarea
            v-model="editMetadataForm.notes"
            label="Notes"
            rows="3"
            dense
            outlined
          />
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="cancelEditMetadata">Cancel</v-btn>
        <v-btn color="primary" @click="saveEditMetadata">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

    <!-- Simple preview dialog (kept) -->
    <v-dialog v-model="preview.visible" max-width="900px">
      <v-card>
        <v-toolbar flat>
          <!-- <v-toolbar-title>{{ preview.item?.filename }}</v-toolbar-title> -->
          <v-toolbar-title v-if="preview.item"> {{ preview.item.filename }} </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="previewClose"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text>
          <div v-if="preview.item">
            <v-img v-if="isImage(preview.item) && preview.item" :lazy-src="preview.item.thumbnail" :src="preview.item.previewUrl || preview.item.downloadUrl" contain max-height="600">
              <template v-slot:placeholder>
                <v-row
                  class="fill-height ma-0"
                  align="center"
                  justify="center"
                >
                  <v-progress-circular
                    indeterminate
                    color="grey lighten-5"
                  ></v-progress-circular>
                </v-row>
              </template>

            </v-img>
            <div v-else-if="isPdf(preview.item)" class="d-flex justify-center">
              <iframe
                v-if="preview.item.downloadUrl"
                :src="preview.item.downloadUrl"
                style="width:100%; height:600px; border: none;"
              ></iframe>
               <div v-else class="text-center">
                <v-icon x-large>mdi-file-pdf-box</v-icon>
                <div class="mt-2">{{ preview.item.filename }}</div>
              </div>
            </div>

            <div v-else class="text-center">
              <v-icon x-large>mdi-file-document-outline</v-icon>
              <div class="mt-2">{{ preview.item.filename }}</div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
let localIdCounter = 0;
let pendingIdCounter = 0;
import { authHeader } from '../_helpers/auth-header.js'
import { streamItems } from '../_helpers/stream.js'



export default {
  name: 'RepairGarageDialog',
  props: { value: { type: Boolean, default: false }, showTrigger: { type: Boolean, default: true } },
  data() {
    return {
      visible: false,
      // Existing items
      items: [],
      page: 1,
      perPage: 8,

      // Upload / pending
      isDragOver: false,
      fileInputBuffer: [],
      pendingFiles: [], // [{ _id, file: File, type: 'image'|'pdf'|'other', meta: {key:val} } ]
      uploading: false,
      previewUrl: null,

      // Filters and sort
      filters: { q: '', type: null },
      fileTypeOptions: ['image', 'pdf', 'other'],
      sort: { value: 'created_at_desc', label: 'Newest' },
      sortOptions: [
        { value: 'created_at_desc', label: 'Newest' },
        { value: 'created_at_asc', label: 'Oldest' },
        { value: 'name_asc', label: 'Name A–Z' },
        { value: 'name_desc', label: 'Name Z–A' }
      ],

      // Preview
      preview: { visible: false, item: null },

      // Edit pending metadata dialog
      editPendingDialog: { visible: false, item: null, meta: {filename: '', tag: null, notes: ''} },
      defaultTags: ['invoice', 'photo', 'report', 'diagnostics', 'part-number'],
      newTag: '',
      tagAddMenu: false,
   
      editMetadataDialog: false,
      editMetadataItem: null,
      editMetadataForm: {
        filename: '',
        tag: '',
        notes: '',
      },
      

    };
  },
  computed: {
    pageCount() { return Math.max(1, Math.ceil(this.filteredItems.length / this.perPage)); },
    pagedItems() {
      const start = (this.page - 1) * this.perPage;
      return this.sortedItems.slice(start, start + this.perPage);
    },
    filteredItems() {
      const q = (this.filters.q || '').toLowerCase().trim();
      return this.items.filter(it => {
        if (this.filters.type) {
          const t = this.fileTypeOf(it);
          if (this.filters.type === 'image' && t !== 'image') return false;
          if (this.filters.type === 'pdf' && t !== 'pdf') return false;
          if (this.filters.type === 'other' && (t === 'image' || t === 'pdf')) return false;
        }
        if (!q) return true;
        if ((it.filename || '').toLowerCase().includes(q)) return true;
        try {
          const m = JSON.stringify(it.metadata || {});
          if (m.toLowerCase().includes(q)) return true;
        } catch(e) {
          console.warn('metadata stringify error', e);
        }
        return false;
      });
    },
    sortedItems() {
      const arr = [...this.filteredItems];
      switch (this.sort.value) {
        case 'created_at_asc': arr.sort((a,b) => new Date(a.fetched_at) - new Date(b.fetched_at)); break;
        case 'created_at_desc': arr.sort((a,b) => new Date(b.fetched_at) - new Date(a.fetched_at)); break;
        case 'name_asc': arr.sort((a,b) => (a.filename||'').localeCompare(b.filename||'')); break;
        case 'name_desc': arr.sort((a,b) => (b.filename||'').localeCompare(a.filename||'')); break;
      }
      return arr;
    },
    isImageFile() {
      const f = this.editPendingDialog?.item?.file;
      if (!f) return false;
      // Prefer MIME type if available
      if (f.type) return f.type.startsWith('image/');
      // Fallback to extension check if you only have a filename
      const name = this.editPendingDialog.meta?.filename || '';
      return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
    },
    tagIndex: {
      get() {
        const tag = this.editPendingDialog.meta.tag;
        if (!tag) return null;
        const idx = this.defaultTags.indexOf(tag);
        return idx === -1 ? null : idx;
      },
      set(index) {
        if (index == null || index < 0 || index >= this.defaultTags.length) {
          this.editPendingDialog.meta.tag = null;
        } else {
          this.editPendingDialog.meta.tag = this.defaultTags[index];
        }
      }
    },
  },
  watch: {
    value(val) { this.visible = val; },
    visible(val) { this.$emit('input', val); if (val) this.fetchItems(); },
    'editPendingDialog.item.file': {
      immediate: true,
      handler(file) {
        if (this.previewUrl) {
          URL.revokeObjectURL(this.previewUrl);
          this.previewUrl = null;
        }
        if (file && file instanceof File && file.type.startsWith('image/')) {
          this.previewUrl = URL.createObjectURL(file);
        }
      },
    },
  },
  beforeDestroy() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
  methods: {
    open() { this.visible = true; },
    close() { this.visible = false; },

    // Drag-n-drop handlers
    onDragOver() { this.isDragOver = true; },
    onDragLeave() { this.isDragOver = false; },
    onDrop(e) {
      this.isDragOver = false;
      const files = Array.from(e.dataTransfer?.files || []);
      this.addPendingFiles(files);
    },

    onFilesSelected() {
      const files = Array.from(this.fileInputBuffer || []);
      this.addPendingFiles(files);
      // clear input
      this.fileInputBuffer = [];
    },

    openCamera() {
      // Trigger the hidden input to open camera on supporting devices
      this.$refs.cameraInput.click();
    },

    onCameraCaptured(event) {
      const input = event.target;
      const files = input.files ? Array.from(input.files) : [];
      if (files.length) {
        this.addPendingFiles(files);
      }
      // clear value so the same camera capture can be used again
      input.value = '';
    },
    addPendingFiles(files) {
      if (!files.length) return;
      files.forEach(file => {
        const type = this.inferFileType(file.name);
        this.pendingFiles.push({
          _id: ++pendingIdCounter,
          file,
          type,
          meta: {filename: file.name, tag:null, notes:'' }
        });
      });
    },

    removePending(id) {
      this.pendingFiles = this.pendingFiles.filter(p => p._id !== id);
    },
    async deleteItem(item) {
      if (!item || !item.docId) return;

      // Optional: user confirmation
      const confirmed = window.confirm(`Delete document "${item.filename}"?`);
      if (!confirmed) return;

      try {
        const res = await this.$api.delete(`/repairs/garage/document/${item.docId}`);
        const ok = res && (res.data === true || res.data === 'true');

        if (ok) {
          // Remove from items list
          this.items = this.items.filter(it => it.docId !== item.docId);

          // Adjust pagination if current page becomes empty
          if (this.page > this.pageCount) {
            this.page = this.pageCount;
          }
        } else {
          console.warn('Delete API returned false for', item.docId);
        }
      } catch (err) {
        console.error('deleteItem error', err);
      }
    },
    openEditPending(pf) {
      // Clone metadata so edits are not applied until save
      const meta = { ...(pf.meta || {}) };
      // initialize editable keys mapping
      const filename = meta.filename || pf.file?.name || pf.filename || 'untitled';
      const tag = meta.tag || null;
      const notes = meta.notes || '';
      this.editPendingDialog = { visible: true, item: pf, meta: { filename, tag, notes } };
      this.newTag = ''; 
    },

    savePendingMeta() { 
      const pf = this.editPendingDialog.item;
      if (!pf) { this.editPendingDialog.visible = false; return; }
      const { filename, tag, notes } = this.editPendingDialog.meta || {};
      // Apply back into pending file metadata
      pf.meta = { filename: filename || pf.file?.name || 'untitled', tag: tag || null, notes: notes || '' };

      // If filename changed, recreate File with same content/type and new name
      try {
        const currentName = pf.file?.name;
        if (pf.file && filename && filename !== currentName) {
          // Create a new File; preserve type and lastModified
          const blob = pf.file;
          const newFile = new File([blob], filename, { type: pf.file.type, lastModified: pf.file.lastModified });
          pf.file = newFile;
        }
      } catch (e) {
        // If File constructor not supported (older browsers), fall back to Blob rename approach is limited
        console.warn('Renaming file failed, will keep original filename for the binary, but metadata will carry the new name', e);
      }

      this.editPendingDialog.visible = false;
    },
    addNewTag() {
      const tag = (this.newTag || '').trim();
      if (!tag) return;

      if (!this.defaultTags.includes(tag)) {
        this.defaultTags.push(tag);
      }

      this.editPendingDialog.meta.tag = tag; // store text
      this.newTag = '';
      this.tagAddMenu = false
    },

    async uploadPending() {
      if (!this.pendingFiles.length) return;
      this.uploading = true;
      try {
        const repairId = this.$route.params.repairId;
        const form = new FormData();

        // Append files (order matters; metadataList aligns with this order)
        this.pendingFiles.forEach(pf => {
          form.append('files', pf.file, pf.file.name);
        });

        // Build metadataList: each entry is either an object or null (when no custom metadata)
        

        const metadataList = this.pendingFiles.map(
          pf => { 
            const m = pf.meta || {};
            return { filename: m.filename || pf.file.name, tag: m.tag || null, notes: m.notes || '' };
           });

        form.append('metadata', JSON.stringify(metadataList));

        await this.$api.post(`/repairs/garage/${repairId}/upload`, form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        await this.fetchItems();
        this.pendingFiles = [];
        this.$emit('uploaded');
      } catch (err) {
        console.error('uploadPending error', err);
      } finally {
        this.uploading = false;
      }
    },

    async fetchItems() {
      try {
        const repairId = this.$route.params.repairId;

        this.items = [];
        const token = authHeader(); // or use axios stored auth
        const headers = token ? { Authorization: `${token}` } : {};
        streamItems(`${this.$api.defaults.baseURL}/repairs/garage/${repairId}/items`, { headers },
          (item) => {
            const base = {
              _localId: ++localIdCounter,
              filename: item.filename,
              downloadUrl: item.downloadUrl,
              uuid: item.uuid,
              metadataUrl: item.metadata,
              metadata: {},
              previewUrl: null,
              thumbnail: item.thumbnail,
              notes: item.notes,
              tag: item.tag,
              docId: item.id,
              fileType: this.inferFileType(item.filename),
              fetched_at:  item.upload_at || new Date().toISOString(),
            };
            if (base.fileType === 'image') base.previewUrl = base.downloadUrl;
            this.items.push(base);
          },
          (err, ctx) => { console.error('stream error', err, ctx); }
        );

      } catch (err) {
        console.error('fetchItems error', err);
      }
    },

    openEditMetadataDialog(item) {
        this.editMetadataItem = item
        this.editMetadataForm = {
          filename: item.filename || '',
          tag: item.tag || '',
          notes: item.notes || '',
        }
        this.editMetadataDialog = true
      },
    
      async saveEditMetadata() {
        if (!this.editMetadataItem) return
    
        const docId = this.editMetadataItem.docId
        const payload = {
          filename: this.editMetadataForm.filename,
          tag: this.editMetadataForm.tag,
          notes: this.editMetadataForm.notes,
        }
    
        try {
          // Adjust to your HTTP client (axios, this.$axios, this.$http, etc.)
          await this.$api.patch(`/repairs/garage/document/${docId}`, payload)
    
          // Update local item to reflect new metadata
          Object.assign(this.editMetadataItem, payload)
    
          this.editMetadataDialog = false
          this.editMetadataItem = null
        } catch (e) {
          // Optional: handle error with snackbar/dialog
          console.error('Failed to update document metadata', e)
        }
      },
    
      cancelEditMetadata() {
        this.editMetadataDialog = false
        this.editMetadataItem = null
      },

    inferFileType(filename = '') {
        const fn = filename.toLowerCase();
        if (fn.endsWith('.pdf')) return 'pdf';
        if (fn.match(/\.(jpe?g|png|gif|webp|bmp|heic)$/)) return 'image';
        return 'other';
        //   if (/\.(jpe?g|png|gif|webp|bmp|heic)$/.test(fn)) return 'image';
        //   if (/\.(pdf)$/.test(fn)) return 'pdf';
        //   return 'other';
    },

    fileTypeOf(item) { return item.fileType || this.inferFileType(item.filename); },
    isImage(item) { return this.fileTypeOf(item) === 'image'; },
    isPdf(item) { return this.fileTypeOf(item) === 'pdf'; },
    formatDate(src) { if (!src) return ''; const d = new Date(src); return d.toLocaleString(); },

    applyFilters() { this.page = 1 },
    setSort(option) { this.sort = option },

    // Actions for existing items
    downloadItem(item) {
      if (!item || !item.downloadUrl) return;
      window.open(item.downloadUrl, '_blank');
    },

    previewItem(item) {
      this.preview.item = item;
      this.preview.visible = true;
    },
    previewClose() {
      this.preview.item = null;
      this.preview.visible = false;
    },

    notImplemented(msg) {
      // Replace with your snackbar/toast if available
      console.info(msg);
    },

    copyUrl(item) {
      if (!item || !item.downloadUrl) return;
//       navigator.clipboard.writeText(item.downloadUrl).then(() => {
        // optionally show toast
 //        console.log(item.downloadUrl)
 //      });
    },
  }
};
</script>

<style scoped>
.upload-dropzone {
  border: 2px dashed #c7c7c7;
  border-radius: 8px;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.upload-dropzone--dragover {
  background-color: #f5f9ff;
  border-color: #1976d2;
}
</style>
